import { useEffect, useState } from 'react';
import type { useAdAmsApi } from '../../api/useAdAmsApi';
import type { PlannedUpload } from './tree';

type AdAmsApi = ReturnType<typeof useAdAmsApi>;

/** Files sent at the same time; each one still goes part after part. */
export const UPLOAD_CONCURRENCY = 4;

export interface UploadProgress {
  /** Files finished, sent or failed. */
  done: number;
  total: number;
  /** The file going for the longest (usually the largest). */
  current: string;
  /** How much of `current` went, from 0 to 1. */
  fraction: number;
  /** Files going besides `current`. */
  others: number;
  /** How much of the files going went, summed: the bar's part between done and done + 1. */
  partial: number;
}

export interface UploadSummary {
  created: number;
  versioned: number;
  unchanged: number;
  removed: number;
  failed: { path: string; message: string }[];
  ignored: string[];
  /** Releases whose every file failed, so they changed nothing ('' for one without a label). */
  unpublished: string[];
  /** Releases that changed something, in order: their details are read from the audit. */
  published: { publishId: string; packageLabel: string | null }[];
}

/**
 * A package publish of one application. Kept outside React, so it goes on
 * while the user opens other tabs and pages of Sheriff (not across a reload).
 */
export interface UploadJob {
  appId: string;
  /** `<code>_<name>`, for the indicator shown on every page. */
  title: string;
  /** Set while it runs. */
  progress: UploadProgress | null;
  /** Set once it ended, until dismissed. */
  summary: UploadSummary | null;
}

let jobs: readonly UploadJob[] = [];
const listeners = new Set<() => void>();

const notify = () => listeners.forEach(listener => listener());

const update = (appId: string, change: Partial<UploadJob>) => {
  jobs = jobs.map(job => (job.appId === appId ? { ...job, ...change } : job));
  notify();
};

/** The jobs, running and ended, re-rendering on every change. */
export function useUploadJobs(): readonly UploadJob[] {
  const [snapshot, setSnapshot] = useState(jobs);
  useEffect(() => {
    const listener = () => setSnapshot(jobs);
    listeners.add(listener);
    listener();
    return () => {
      listeners.delete(listener);
    };
  }, []);
  return snapshot;
}

/** Forgets an ended job: its summary is no longer shown. */
export function dismissUploadJob(appId: string): void {
  jobs = jobs.filter(job => job.appId !== appId || job.progress);
  notify();
}

function errorMessage(error: unknown): string {
  // fetch rejects with a TypeError when the connection drops.
  return error instanceof TypeError
    ? 'Conexão interrompida durante o envio, mesmo após novas tentativas.'
    : (error as Error).message;
}

/** Consecutive items sharing a package label: the releases, already in release order. */
function releasesOf(items: PlannedUpload[]): PlannedUpload[][] {
  const releases: PlannedUpload[][] = [];
  for (const item of items) {
    const last = releases[releases.length - 1];
    if (last && last[0].packageLabel === item.packageLabel) {
      last.push(item);
    } else {
      releases.push([item]);
    }
  }
  return releases;
}

/**
 * Publishes a package: uploads up to UPLOAD_CONCURRENCY files at a time,
 * collecting a per-file summary. Each release ends, once all its files are
 * done, by removing whatever that release does not contain; the next one
 * starts only then. One job per application at a time.
 */
export async function startUploadJob(
  api: AdAmsApi,
  appId: string,
  title: string,
  items: PlannedUpload[],
  ignored: string[],
  /** The publisher confirmed it removes most of the content: the last release, which does, records it. */
  confirmedRemoval = false,
): Promise<void> {
  if (jobs.some(job => job.appId === appId && job.progress)) {
    throw new Error('Já há um envio em andamento nesta aplicação.');
  }
  const result: UploadSummary = {
    created: 0,
    versioned: 0,
    unchanged: 0,
    removed: 0,
    failed: [],
    ignored,
    unpublished: [],
    published: [],
  };
  let done = 0;
  // Files going, in the order they started, with how much of each went.
  const going = new Map<string, number>();
  const report = () => {
    const [current, fraction] = going.entries().next().value ?? ['', 0];
    let partial = 0;
    going.forEach(value => (partial += value));
    update(appId, {
      progress: {
        done,
        total: items.length,
        current,
        fraction,
        others: Math.max(going.size - 1, 0),
        partial,
      },
    });
  };
  jobs = [
    ...jobs.filter(job => job.appId !== appId),
    { appId, title, progress: null, summary: null },
  ];
  report();

  // Why nothing more can be published now (another publish, a change started):
  // the files not sent yet would all be refused the same way.
  let blocked: string | undefined;

  /** Whether the file was stored (or found unchanged). */
  const upload = async (item: PlannedUpload, publishId: string): Promise<boolean> => {
    const label = `${item.packageLabel ? `[${item.packageLabel}] ` : ''}${item.relativePath}`;
    let stored = false;
    going.set(label, 0);
    report();
    try {
      const file = await item.getFile();
      const uploaded = await api.uploadFile(
        appId,
        item.relativePath,
        file,
        publishId,
        item.packageLabel,
        sent => {
          going.set(label, sent / file.size);
          report();
        },
      );
      if (uploaded.created) {
        result.created += 1;
      } else if (uploaded.unchanged) {
        result.unchanged += 1;
      } else {
        result.versioned += 1;
      }
      stored = true;
    } catch (error) {
      result.failed.push({ path: label, message: errorMessage(error) });
      if ((error as { status?: number }).status === 423) {
        blocked = errorMessage(error);
      }
    }
    going.delete(label);
    done += 1;
    report();
    return stored;
  };

  /** A file not sent, refused the way the ones before it were. */
  const refuse = (item: PlannedUpload, message: string) => {
    result.failed.push({
      path: `${item.packageLabel ? `[${item.packageLabel}] ` : ''}${item.relativePath}`,
      message,
    });
    done += 1;
    report();
  };

  /** Sends a release's files, UPLOAD_CONCURRENCY at a time; answers how many were stored. */
  const sendRelease = async (release: PlannedUpload[], publishId: string) => {
    const queue = [...release];
    let stored = 0;
    await Promise.all(
      Array.from({ length: Math.min(UPLOAD_CONCURRENCY, queue.length) }, async () => {
        for (let item = queue.shift(); item; item = queue.shift()) {
          if (blocked) {
            refuse(item, blocked);
          } else if (await upload(item, publishId)) {
            stored += 1;
          }
        }
      }),
    );
    return stored;
  };

  const releases = releasesOf(items);
  for (const [index, release] of releases.entries()) {
    // Groups the release's audit rows into one entry.
    const publishId = crypto.randomUUID();
    const packageLabel = release[0].packageLabel;
    if (blocked) {
      // Refused like the rest: nothing of it is sent.
      for (const item of release) {
        refuse(item, blocked);
      }
      result.unpublished.push(packageLabel ?? '');
      continue;
    }
    try {
      const stored = await sendRelease(release, publishId);

      // A release that stored nothing leaves the application as it was; one
      // cut short is not pruned, which would be refused the same way.
      if (stored === 0) {
        result.unpublished.push(packageLabel ?? '');
        continue;
      }
      result.published.push({ publishId, packageLabel: packageLabel ?? null });
      if (blocked) {
        continue;
      }
      // Paths that failed still count as part of the release, so a network
      // error keeps the file's previous version instead of removing it.
      try {
        const pruned = await api.prunePackage(
          appId,
          release.map(item => item.relativePath),
          publishId,
          packageLabel,
          ...(confirmedRemoval && index === releases.length - 1 ? [true] : []),
        );
        result.removed += pruned.removed;
      } catch (error) {
        result.failed.push({
          path: `${packageLabel ? `[${packageLabel}] ` : ''}remoção dos itens fora do pacote`,
          message: errorMessage(error),
        });
      }
    } finally {
      // Whatever came of it, the application is free for the next publish
      // (or once idle, if this does not get through).
      try {
        await api.endPublish(appId, publishId);
      } catch {
        // Nothing to tell: the backend frees it in time.
      }
    }
  }

  update(appId, { progress: null, summary: result });
}
